import {
  useMemo,
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, type SxProps } from '@mui/material';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type Row,
  type SortingState,
} from '@tanstack/react-table';

import type { Option } from 'shared/models/common';
import type { BaseTaskTodo, InitTodoForm } from 'models';
import { selectStatusesOptions } from 'store/todo/selectors';

import { EntityType } from 'shared/constants';
import { TodoEditForm, TodoCreateForm, PageTitle } from 'containers';
import DatePicker from 'components/controls/date-picker';
import Grid from 'shared/components/Grid';
import Search from 'shared/components/Search';
import Select, { type SelectChangeEvent } from 'shared/components/Select';
import TableFilterComponent, {
  type Option as FilterOption,
} from 'shared/components/TableFilterComponent';
import StyledFiltersBar from 'shared/components/styled/FiltersBar';
import { GridWrapper } from 'shared/components/styled';
import { DateRangeType } from 'shared/constants';
import AttachIcon from 'shared/icons/Attach';
import useEntityDialogState from 'shared/hooks/useEntityDialogState';
import useSelector from 'shared/hooks/useSelector';
import formatDatetime from 'shared/utils/formatDatetime';
import getSelectHelpers from 'shared/utils/getSelectHelpers';
import safeParseHtml from 'shared/utils/safeParseHtml';
import { createStatusOptionFilterFn } from 'shared/utils/grid';

import { NotificationBadge } from 'features/notification';

import { Divider, Label, Status } from './style';

const columnHelper = createColumnHelper<BaseTaskTodo>();

function getDefaultStatuses(
  availableStatusesOptions: FilterOption<number>[],
  defaultStatusesValue: number[]
) {
  const availableStatuses = availableStatusesOptions.map(
    (availableStatusesOption) => availableStatusesOption.value
  );
  const defaultStatuses = defaultStatusesValue.filter((value) =>
    availableStatuses.includes(value)
  );

  if (defaultStatuses.length) return defaultStatuses;

  return availableStatuses;
}

const ControlWrapper = ({
  children,
  sx,
}: PropsWithChildren<{ sx?: SxProps }>) => (
  <Stack alignItems="flex-start" spacing={1} sx={sx}>
    {children}
  </Stack>
);

interface Props {
  isAbleToCreateNewTask: boolean | null;
  upload: boolean;
  refreshTodoList: () => void;
  tasks: BaseTaskTodo[];
  startDate: Date | null;
  endDate: Date | null;
  dateFilter: DateRangeType;
  searchTerm: string;
  handleChangeDateFilter: (event: SelectChangeEvent<DateRangeType>) => void;
  handleChangeStartDate: (date: Date | null) => void;
  handleChangeEndDate: (date: Date | null) => void;
  handleChangeSearchTerm: (event: ChangeEvent<HTMLInputElement>) => void;
}

const TodoDetails = ({
  isAbleToCreateNewTask,
  upload,
  tasks,
  dateFilter,
  searchTerm,
  startDate,
  endDate,
  handleChangeEndDate,
  handleChangeStartDate,
  refreshTodoList,
  handleChangeDateFilter,
  handleChangeSearchTerm,
}: Props) => {
  const { t } = useTranslation();

  const dateFilterOptions = useRef<Option<DateRangeType>[]>([
    {
      Value: DateRangeType.ThisYear,
      Label: t('#portal.task.todo.filter.label.thisyear'),
    },
    {
      Value: DateRangeType.ThisMonth,
      Label: t('#portal.task.todo.filter.label.thismonth'),
    },
    {
      Value: DateRangeType.ThisWeek,
      Label: t('#portal.task.todo.filter.label.thisweek'),
    },
    {
      Value: DateRangeType.Today,
      Label: t('#portal.task.todo.filter.label.today'),
    },
  ]);

  const availableStatuses = useSelector(selectStatusesOptions);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => [
    {
      id: 'Status_Label',
      value: getDefaultStatuses(availableStatuses, [3, 4, 7]),
    },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'DueDate', desc: false },
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('Subject', {
        header: t('#portal.task.todo.grid.header.label.subject'),
        cell: (info) => {
          const value = info.getValue();

          return (
            <NotificationBadge
              entityId={info.row.original.Id}
              entityType={EntityType.Task}
            >
              {value && safeParseHtml(value)}
            </NotificationBadge>
          );
        },
        meta: { sx: { width: '22%' } },
      }),
      columnHelper.accessor('HasFiles', {
        enableSorting: false,
        header: t('#portal.task.todo.grid.header.label.attachments'),
        meta: { align: 'center', sx: { width: '10%' } },
        cell: (info) =>
          info.getValue() && <AttachIcon sx={{ fontSize: '1.3rem' }} />,
      }),
      columnHelper.accessor('Executor.Name', {
        header: t('#portal.task.todo.grid.header.label.executor'),
        meta: { sx: { width: '17%' } },
      }),
      columnHelper.accessor('Priority.Label', {
        header: t('#portal.task.todo.grid.header.label.priority'),
        meta: { sx: { width: '17%' } },
      }),
      columnHelper.accessor('DueDate', {
        header: t('#portal.task.todo.grid.header.label.due'),
        cell: (info) => formatDatetime(info.getValue()),
        meta: { sx: { width: '17%' } },
        sortingFn: 'datetime',
      }),
      columnHelper.accessor('Status.Label', {
        header: t('#portal.task.todo.grid.header.label.status'),
        filterFn: createStatusOptionFilterFn<BaseTaskTodo>(),
        cell: (info) => (
          <Status status={info.row.original.Status?.Value ?? null}>
            {info.getValue()}
          </Status>
        ),
        meta: {
          sx: { width: '17%' },
          filterComponent: ({ column, table }) => (
            <TableFilterComponent
              column={column}
              table={table}
              showSelectAllOption
              selectAllLabel={'Valitse kaikki'}
              options={availableStatuses}
            />
          ),
        },
      }),
    ],
    [availableStatuses, t]
  );

  const table = useReactTable({
    data: tasks,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  const taskModalState = useEntityDialogState();

  const [openTodoCreateModal, setOpenTodoCreateModal] = useState(false);
  const [initForm, setInitForm] = useState<InitTodoForm | null>(null);

  const selectHelpers = useMemo(
    () => getSelectHelpers(dateFilterOptions.current),
    []
  );

  const handleClickRow = (
    _event: MouseEvent<HTMLTableRowElement>,
    row: Row<BaseTaskTodo>
  ) => {
    taskModalState.onOpen(row.original.Id);
  };

  const handleOpenCreateModal = () => {
    setOpenTodoCreateModal(true);
  };

  const handleCloseCreateModal = (upload?: boolean) => {
    setOpenTodoCreateModal(false);

    if (upload) {
      refreshTodoList();
    }
  };

  const handleCloseEditModal = useCallback(
    (update?: boolean) => {
      taskModalState.onClose();

      if (update) {
        refreshTodoList();
      }
    },
    [taskModalState, refreshTodoList]
  );

  const handleSetInitTodoForm = useCallback(async (initForm: InitTodoForm) => {
    setInitForm(initForm);

    handleCloseCreateModal(true);

    setTimeout(() => {
      handleOpenCreateModal();
    }, 2500);

    /* eslint-disable-next-line */
  }, []);

  const handleSetInitEditTodoForm = useCallback((initForm: InitTodoForm) => {
    setInitForm(initForm);

    handleCloseEditModal(true);

    setTimeout(() => {
      handleOpenCreateModal();
    }, 2500);
  }, []);

  return (
    <>
      <Stack alignItems="center" direction="row" mb={2} spacing={2}>
        <PageTitle />
        {isAbleToCreateNewTask && (
          <Button
            color="success"
            sx={{ fontWeight: 600 }}
            onClick={() => {
              setInitForm(null);
              handleOpenCreateModal();
            }}
          >
            UUSI TEHTÄVÄ
          </Button>
        )}
      </Stack>
      <StyledFiltersBar
        alignItems={{
          xs: 'flex-start',
          md: 'flex-end',
        }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        justifyContent="space-between"
        spacing={2.5}
      >
        <Stack
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={2.5}
        >
          <ControlWrapper>
            <Label>Ajankohta:</Label>
            <Select
              value={dateFilter}
              options={selectHelpers.options}
              getOptionLabel={selectHelpers.getOptionLabel}
              sx={{ width: 'auto' }}
              onChange={handleChangeDateFilter}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Label>{t('#portal.task.todo.filter.label.period')}:</Label>
            <Stack alignItems="center" direction="row" spacing={1.5}>
              <DatePicker
                date={startDate}
                maxDate={endDate}
                handleChange={handleChangeStartDate}
              />
              <Divider>&mdash;</Divider>
              <DatePicker
                date={endDate}
                minDate={startDate}
                handleChange={handleChangeEndDate}
              />
            </Stack>
          </ControlWrapper>
        </Stack>

        <ControlWrapper sx={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
          <Search
            debounce
            placeholder="Hae"
            value={searchTerm}
            onChange={handleChangeSearchTerm}
          />
        </ControlWrapper>
      </StyledFiltersBar>
      <GridWrapper>
        <Grid table={table} onClickRow={handleClickRow} />
      </GridWrapper>
      {taskModalState.open && (
        <TodoEditForm
          handleSetInitTodoForm={handleSetInitEditTodoForm}
          taskId={taskModalState.id}
          handleClear={taskModalState.onClearId}
          handleClose={handleCloseEditModal}
        />
      )}
      {openTodoCreateModal && (
        <TodoCreateForm
          handleClose={handleCloseCreateModal}
          initForm={initForm}
          handleSetInitTodoForm={handleSetInitTodoForm}
        />
      )}
    </>
  );
};

export default TodoDetails;
